import React, { useRef, useState, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { primeTickerModalKeyboard } from '../../utils/tickerModalKeyboard';

const ITEM_HEIGHT = 44; // Height of each item in the picker
const VISIBLE_ITEMS = 5; // Number of items visible at once
const DAMPING = 0.93; // Momentum damping (slightly faster coast than the original 0.92)
const SNAP_VELOCITY_THRESHOLD = 0.5; // Minimum velocity to trigger snap
const TOUCH_SENSITIVITY = 1.35; // Mildly faster than the original 1.0
const VELOCITY_SCALE = 1.1; // Light momentum boost on release
const DRAG_THRESHOLD = 5; // Minimum movement to trigger drag
/** Odd number of list copies so the wheel can wrap seamlessly. */
const LOOP_COPIES = 5;

export function wrapIndex(index, length) {
  if (length <= 0) return 0;
  return ((index % length) + length) % length;
}

/**
 * A smooth, iOS-style picker wheel component with momentum scrolling.
 * Drag/momentum wrap continuously so scrolling past the last item returns to the first.
 *
 * @param {Object} props
 * @param {Array} props.items - Array of items to display in the picker
 * @param {*} props.value - Currently selected value
 * @param {Function} props.onChange - Callback when selection changes (value) => void
 * @param {Function} props.renderItem - Optional custom item renderer (item, isSelected) => React.Node
 * @param {Function} props.getItemKey - Optional function to extract key from item (item) => string
 * @param {Function} props.getItemLabel - Optional function to extract label from item (item) => string
 * @param {boolean} props.disabled - Whether the picker is disabled
 * @param {string} props.placeholder - Placeholder text when no value
 * @param {Function} props.onEmptyClick - Optional callback when clicked with no items
 * @param {Function} props.onSearchClick - Optional callback for search action
 * @param {string} props.buttonStyle - Additional styles for the button
 * @param {boolean} props.showDropdown - Whether to show dropdown on tap (default: true)
 * @param {number} props.itemHeight - Height of each item (default: 44)
 * @param {number} props.visibleItems - Number of visible items (default: 5)
 */
export const ScrollPickerSelector = ({
  items = [],
  value,
  onChange,
  renderItem,
  getItemKey,
  getItemLabel,
  disabled = false,
  placeholder = 'N/A',
  onEmptyClick,
  onSearchClick,
  buttonStyle = {},
  showDropdown: enableDropdown = true,
  itemHeight = ITEM_HEIGHT,
  visibleItems = VISIBLE_ITEMS,
  ariaLabel,
  testId,
}) => {
  const { colors } = useTheme();

  const getKey = getItemKey || ((item) => (typeof item === 'string' ? item : item.id || item.value));
  const getLabel = getItemLabel || ((item) => (typeof item === 'string' ? item : item.label || item.name || item.value));

  const currentIndex = items.findIndex((item) => getKey(item) === getKey(value));
  const pickerHeight = itemHeight * visibleItems;
  const itemCount = items.length;
  const canLoop = itemCount > 1;
  const middleCopyStart = Math.floor(LOOP_COPIES / 2) * Math.max(itemCount, 1);
  const cycleHeight = itemCount * itemHeight;

  const loopedRows = useMemo(() => {
    if (itemCount === 0) return [];
    const copies = canLoop ? LOOP_COPIES : 1;
    const rows = [];
    for (let copy = 0; copy < copies; copy += 1) {
      items.forEach((item, logicalIndex) => {
        rows.push({
          item,
          logicalIndex,
          absoluteIndex: copy * itemCount + logicalIndex,
          key: `${copy}:${getKey(item)}`,
        });
      });
    }
    return rows;
  }, [items, itemCount, canLoop, getKey]);

  const getTranslateForAbsolute = useCallback((absoluteIndex) => (
    -absoluteIndex * itemHeight + (pickerHeight / 2 - itemHeight / 2)
  ), [itemHeight, pickerHeight]);

  const absoluteFromTranslate = useCallback((ty) => (
    Math.round(-ty / itemHeight + (visibleItems / 2 - 0.5))
  ), [itemHeight, visibleItems]);

  /** Keep the strip in the middle copy so wrap never runs out of rows. */
  const normalizeTranslate = useCallback((ty) => {
    if (!canLoop || cycleHeight <= 0) return ty;
    let next = ty;
    const midStart = getTranslateForAbsolute(middleCopyStart);
    const midEnd = getTranslateForAbsolute(middleCopyStart + itemCount - 1);
    while (next > midStart + itemHeight / 2) next -= cycleHeight;
    while (next < midEnd - itemHeight / 2) next += cycleHeight;
    return next;
  }, [canLoop, cycleHeight, getTranslateForAbsolute, middleCopyStart, itemCount, itemHeight]);

  const logicalFromTranslate = useCallback((ty) => {
    if (itemCount <= 0) return 0;
    return wrapIndex(absoluteFromTranslate(ty), itemCount);
  }, [absoluteFromTranslate, itemCount]);

  const [isActive, setIsActive] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [translateY, setTranslateY] = useState(() => (
    getTranslateForAbsolute(middleCopyStart + (currentIndex >= 0 ? currentIndex : 0))
  ));
  const [selectedIndex, setSelectedIndex] = useState(currentIndex >= 0 ? currentIndex : 0);
  const [isDragging, setIsDragging] = useState(false);

  const buttonRef = useRef(null);
  const overlayRef = useRef(null);
  const listRef = useRef(null);
  const dropdownRef = useRef(null);
  const touchStartY = useRef(0);
  const lastTouchY = useRef(0);
  const velocity = useRef(0);
  const animationRef = useRef(null);
  const lastTimestamp = useRef(0);
  const touchIdentifier = useRef(null);
  const suppressClickRef = useRef(false);
  const translateYRef = useRef(translateY);
  translateYRef.current = translateY;

  const commitSelection = useCallback((logicalIndex) => {
    const selectedItem = items[logicalIndex];
    if (selectedItem && getKey(selectedItem) !== getKey(value)) {
      onChange(selectedItem);
      if (window.navigator?.vibrate) {
        window.navigator.vibrate(10);
      }
    }
  }, [items, value, onChange, getKey]);

  const snapToNearest = useCallback((ty) => {
    if (itemCount <= 0) return;
    const abs = absoluteFromTranslate(ty);
    const logical = wrapIndex(abs, itemCount);
    const canonicalAbs = canLoop ? middleCopyStart + logical : Math.max(0, Math.min(itemCount - 1, abs));
    setSelectedIndex(logical);
    setTranslateY(getTranslateForAbsolute(canonicalAbs));
    setIsDragging(false);
    commitSelection(logical);
    setTimeout(() => setIsActive(false), 200);
  }, [
    itemCount,
    absoluteFromTranslate,
    canLoop,
    middleCopyStart,
    getTranslateForAbsolute,
    commitSelection,
  ]);

  useEffect(() => {
    if (!isDragging && !isActive) {
      const abs = canLoop ? middleCopyStart + selectedIndex : selectedIndex;
      setTranslateY(getTranslateForAbsolute(abs));
    }
  }, [selectedIndex, isDragging, isActive, canLoop, middleCopyStart, getTranslateForAbsolute]);

  useEffect(() => {
    const newIndex = items.findIndex((item) => getKey(item) === getKey(value));
    if (newIndex >= 0 && newIndex !== selectedIndex && !isDragging) {
      setSelectedIndex(newIndex);
    }
  }, [value, items, selectedIndex, isDragging, getKey]);

  const animateMomentum = useCallback((timestamp) => {
    if (!lastTimestamp.current) {
      lastTimestamp.current = timestamp;
    }

    const deltaTime = timestamp - lastTimestamp.current;
    lastTimestamp.current = timestamp;

    velocity.current *= DAMPING;

    if (Math.abs(velocity.current) < SNAP_VELOCITY_THRESHOLD) {
      snapToNearest(translateYRef.current);
      velocity.current = 0;
      lastTimestamp.current = 0;
      return;
    }

    const newTranslate = normalizeTranslate(
      translateYRef.current + velocity.current * (deltaTime / 16) * VELOCITY_SCALE,
    );
    translateYRef.current = newTranslate;
    setTranslateY(newTranslate);

    const nextLogical = logicalFromTranslate(newTranslate);
    setSelectedIndex((prev) => {
      if (prev !== nextLogical && window.navigator?.vibrate) {
        window.navigator.vibrate(3);
      }
      return nextLogical;
    });

    animationRef.current = requestAnimationFrame(animateMomentum);
  }, [normalizeTranslate, logicalFromTranslate, snapToNearest]);

  const handlePointerDown = useCallback((e) => {
    if (disabled || touchIdentifier.current !== null) return;

    e.preventDefault();
    e.stopPropagation();

    touchIdentifier.current = e.pointerId;
    touchStartY.current = e.clientY;
    lastTouchY.current = e.clientY;
    velocity.current = 0;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {
      console.warn('Pointer capture failed:', err);
    }
  }, [disabled]);

  const handlePointerMove = useCallback((e) => {
    if (touchIdentifier.current === null || e.pointerId !== touchIdentifier.current) return;

    e.preventDefault();

    const currentY = e.clientY;
    const deltaY = (currentY - lastTouchY.current) * TOUCH_SENSITIVITY;

    velocity.current = deltaY;

    if (!isActive && Math.abs(currentY - touchStartY.current) > DRAG_THRESHOLD) {
      setIsActive(true);
      setIsDragging(true);
    }

    if (isActive) {
      const newTranslate = normalizeTranslate(translateYRef.current + deltaY);
      translateYRef.current = newTranslate;
      setTranslateY(newTranslate);

      const nextLogical = logicalFromTranslate(newTranslate);
      if (nextLogical !== selectedIndex) {
        setSelectedIndex(nextLogical);
        if (window.navigator?.vibrate) {
          window.navigator.vibrate(3);
        }
      }
    }

    lastTouchY.current = currentY;
  }, [isActive, selectedIndex, normalizeTranslate, logicalFromTranslate]);

  const handlePointerUp = useCallback((e) => {
    if (touchIdentifier.current === null || e.pointerId !== touchIdentifier.current) return;

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      // Ignore
    }

    touchIdentifier.current = null;

    if (!isActive && Math.abs(e.clientY - touchStartY.current) < DRAG_THRESHOLD) {
      e.preventDefault();
      e.stopPropagation();

      if (onSearchClick) {
        primeTickerModalKeyboard();
        onSearchClick();
      } else if (items.length > 0 && enableDropdown) {
        suppressClickRef.current = true;
        setShowDropdown((prev) => !prev);
      } else if (items.length === 0 && onEmptyClick) {
        suppressClickRef.current = true;
        onEmptyClick();
      }
      return;
    }

    if (Math.abs(velocity.current) > SNAP_VELOCITY_THRESHOLD) {
      lastTimestamp.current = 0;
      animationRef.current = requestAnimationFrame(animateMomentum);
    } else {
      snapToNearest(translateYRef.current);
    }
  }, [isActive, items.length, animateMomentum, enableDropdown, onEmptyClick, onSearchClick, snapToNearest]);

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return undefined;

    button.addEventListener('pointerdown', handlePointerDown);
    button.addEventListener('pointermove', handlePointerMove);
    button.addEventListener('pointerup', handlePointerUp);
    button.addEventListener('pointercancel', handlePointerUp);

    return () => {
      button.removeEventListener('pointerdown', handlePointerDown);
      button.removeEventListener('pointermove', handlePointerMove);
      button.removeEventListener('pointerup', handlePointerUp);
      button.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay || !isActive) return undefined;

    overlay.addEventListener('pointermove', handlePointerMove);
    overlay.addEventListener('pointerup', handlePointerUp);
    overlay.addEventListener('pointercancel', handlePointerUp);

    return () => {
      overlay.removeEventListener('pointermove', handlePointerMove);
      overlay.removeEventListener('pointerup', handlePointerUp);
      overlay.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isActive, handlePointerMove, handlePointerUp]);

  const handleItemClick = useCallback((item, e) => {
    e.preventDefault();
    e.stopPropagation();

    onChange(item);
    setShowDropdown(false);
  }, [onChange]);

  useLayoutEffect(() => {
    if (!showDropdown) {
      setDropdownPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const viewportPadding = 8;
      const longestLabel = items.reduce((max, item) => (
        Math.max(max, String(getLabel(item) ?? '').length)
      ), 0);
      const menuWidth = Math.max(rect.width, Math.ceil(longestLabel * 11 + 48), 128);
      const rowHeight = 42;
      const fullHeight = Math.max(rowHeight, items.length * rowHeight);
      const spaceAbove = Math.max(0, rect.top - viewportPadding);
      const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - viewportPadding);
      const fitsAbove = spaceAbove >= fullHeight;
      const fitsBelow = spaceBelow >= fullHeight;
      const openUp = fitsAbove && !fitsBelow
        ? true
        : fitsBelow && !fitsAbove
          ? false
          : spaceAbove > spaceBelow;
      const available = openUp ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(rowHeight, Math.min(fullHeight, available || fullHeight));
      let left = rect.left;
      if (left + menuWidth > window.innerWidth - viewportPadding) {
        left = Math.max(viewportPadding, window.innerWidth - viewportPadding - menuWidth);
      }
      left = Math.max(viewportPadding, left);
      const top = openUp
        ? Math.max(viewportPadding, rect.top - 4 - maxHeight)
        : rect.bottom + 4;
      setDropdownPosition({
        top,
        left,
        width: menuWidth,
        maxHeight,
        openUp,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showDropdown, items.length, getLabel, items]);

  useEffect(() => {
    if (!showDropdown) return undefined;

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)
          && buttonRef.current && !buttonRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, []);

  const handleClick = useCallback((e) => {
    if (disabled) return;
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (onSearchClick) {
      primeTickerModalKeyboard();
      onSearchClick();
      return;
    }
    if (items.length > 0 && enableDropdown) {
      setShowDropdown((prev) => !prev);
      return;
    }
    if (items.length === 0 && onEmptyClick) {
      onEmptyClick();
    }
  }, [disabled, enableDropdown, items.length, onEmptyClick, onSearchClick]);

  const defaultRenderItem = (item, isSelected) => (
    <span
      style={{
        fontSize: '18px',
        fontWeight: isSelected ? '700' : '500',
        color: isSelected ? colors.primary : colors.text,
        textShadow: isSelected ? `0 0 10px ${colors.primary}40` : 'none',
        transition: 'all 0.2s ease',
      }}
    >
      {getLabel(item)}
    </span>
  );

  const itemRenderer = renderItem || defaultRenderItem;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        ref={buttonRef}
        role="button"
        onClick={handleClick}
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel || placeholder}
        aria-haspopup="listbox"
        aria-expanded={showDropdown || isActive}
        data-testid={testId || undefined}
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: '4px',
          padding: '4px 8px',
          minHeight: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? 0.5 : (isActive || showDropdown ? 0.7 : 1),
          cursor: disabled ? 'not-allowed' : 'pointer',
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          ...buttonStyle,
        }}
      >
        <span style={{
          color: colors.primary,
          fontSize: '13px',
          fontWeight: '600',
          whiteSpace: 'nowrap',
        }}
        >
          {value ? getLabel(value) : placeholder}
        </span>
      </div>

      {showDropdown && enableDropdown && !onSearchClick && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          data-testid={testId ? `${testId}-dropdown` : 'scroll-picker-dropdown'}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            margin: 0,
            backgroundColor: colors.bgPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            zIndex: 10001,
            boxSizing: 'border-box',
            minWidth: '128px',
            maxHeight: dropdownPosition.maxHeight,
            overflowY: dropdownPosition.maxHeight < (items.length * 42) ? 'auto' : 'hidden',
            overscrollBehavior: 'contain',
          }}
        >
          {items.map((item, index) => {
            const key = getKey(item);
            const isSelected = key === getKey(value);

            return (
              <div
                key={key}
                role="option"
                aria-selected={isSelected}
                onClick={(e) => handleItemClick(item, e)}
                style={{
                  padding: '10px 16px',
                  fontSize: '15px',
                  fontWeight: isSelected ? '600' : '500',
                  color: isSelected ? colors.primary : colors.text,
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.1s ease',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  borderBottom: index < items.length - 1 ? `1px solid ${colors.border}` : 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.hover || 'rgba(255, 255, 255, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {getLabel(item)}
              </div>
            );
          })}
        </div>,
        document.body,
      )}

      {isActive && items.length > 0 && (
        <div
          ref={overlayRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '280px',
              height: pickerHeight,
              overflow: 'hidden',
              borderRadius: '16px',
              backgroundColor: 'rgba(30, 30, 30, 0.9)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: itemHeight,
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderTop: `1px solid ${colors.border}`,
                borderBottom: `1px solid ${colors.border}`,
                pointerEvents: 'none',
              }}
            />

            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '25%',
                background: 'linear-gradient(to bottom, rgba(30, 30, 30, 0.95), transparent)',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '25%',
                background: 'linear-gradient(to top, rgba(30, 30, 30, 0.95), transparent)',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />

            <div
              ref={listRef}
              style={{
                transform: `translate3d(0, ${translateY}px, 0)`,
                transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                willChange: 'transform',
              }}
            >
              {loopedRows.map((row) => {
                const itemY = row.absoluteIndex * itemHeight + translateY;
                const centerY = pickerHeight / 2;
                const distanceFromCenter = Math.abs(itemY + itemHeight / 2 - centerY);
                const maxDistance = pickerHeight / 2;
                const normalizedDistance = Math.min(distanceFromCenter / maxDistance, 1);

                const opacity = 1 - normalizedDistance * 0.6;
                const scale = 1 - normalizedDistance * 0.15;
                const blur = normalizedDistance * 2;

                const isCurrentlySelected = row.logicalIndex === selectedIndex
                  && Math.abs(distanceFromCenter) < itemHeight;

                return (
                  <div
                    key={row.key}
                    style={{
                      height: itemHeight,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity,
                      transform: `scale3d(${scale}, ${scale}, 1) translateZ(0)`,
                      filter: `blur(${blur}px)`,
                      transition: isDragging ? 'none' : 'all 0.3s ease-out',
                      willChange: 'transform, opacity, filter',
                    }}
                  >
                    {itemRenderer(row.item, isCurrentlySelected)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrollPickerSelector;
